import { useState } from "react"
import { CustomDropdown, adminUpdateClient } from "shared"

export function ClientNotesPage({ data, clientId }): JSX.Element {
	const [noteText, setNoteText] = useState("")
	const [expiryPeriod, setExpiryPeriod] = useState("1_week")
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [lastResult, setLastResult] = useState(null)
	const [error, setError] = useState(null)

	const handleSubmit = async () => {
		if (!noteText.trim()) {
			setError("Please enter note text")
			return
		}

		setIsSubmitting(true)
		setError(null)

		try {
			const expiryTimestamp = getExpiryTimestamp(expiryPeriod)

			const result = await adminUpdateClient({
				clientId,
				updateType: "addNote",
				text: noteText,
				expiry: expiryTimestamp,
			})

			setLastResult(result)
			setNoteText("")
			setExpiryPeriod("1_week")
		} catch (err) {
			setError(err.message || "Failed to add note")
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<div className="account-container">
			<h1 className="bs-h1">Client Notes</h1>
			<div style={{ maxWidth: 600, margin: "20px 0" }}>
				<div style={{ marginBottom: 20 }}>
					<label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>Note Text:</label>
					<textarea
						value={noteText}
						onChange={(e) => setNoteText(e.target.value)}
						placeholder="Enter your note here..."
						rows={4}
						style={{
							width: "100%",
							padding: 10,
							border: "1px solid #ccc",
							borderRadius: 4,
							fontSize: 14,
							fontFamily: "inherit",
							resize: "vertical",
						}}
					/>
				</div>

				<div style={{ marginBottom: 20 }}>
					<label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>Expiry Time:</label>
					<CustomDropdown
						value={expiryPeriod}
						actions={EXPIRY_OPTIONS}
						bestOption="1_week"
						onChange={(e) => {
							setExpiryPeriod(e.target.value)
						}}
					/>
				</div>

				<div style={{ marginBottom: 20 }}>
					<button
						onClick={handleSubmit}
						disabled={isSubmitting || !noteText.trim()}
						style={{
							padding: "10px 20px",
							backgroundColor: isSubmitting || !noteText.trim() ? "#ccc" : "#007bff",
							color: "white",
							border: "none",
							borderRadius: 4,
							cursor: isSubmitting || !noteText.trim() ? "not-allowed" : "pointer",
							fontSize: 14,
							fontWeight: "bold",
						}}>
						{isSubmitting ? "Adding Note..." : "Add Note"}
					</button>
				</div>

				{error && (
					<div
						style={{
							padding: 10,
							backgroundColor: "#fee",
							border: "1px solid #fcc",
							borderRadius: 4,
							color: "#c33",
							marginBottom: 20,
						}}>
						Error: {error}
					</div>
				)}

				{lastResult && (
					<div
						style={{
							padding: 10,
							backgroundColor: "#efe",
							border: "1px solid #cfc",
							borderRadius: 4,
							color: "#363",
							marginBottom: 20,
						}}>
						Success: {lastResult.message || "Note added successfully"}
						{lastResult.note && (
							<div style={{ marginTop: 8, fontSize: 12 }}>
								<strong>Note:</strong> {lastResult.note.text.substring(0, 100)}
								{lastResult.note.text.length > 100 ? "..." : ""}
								<br />
								<strong>Expires:</strong> {new Date(lastResult.note.expiry * 1000).toLocaleString()}
							</div>
						)}
					</div>
				)}

				<div style={{ fontSize: 12, color: "#666" }}>
					<p>Notes are added to the client record and will expire at the specified time.</p>
					<p>Client ID: {clientId}</p>
				</div>
			</div>
		</div>
	)
}

type ExpiryOption = { key: string; label: string }

const EXPIRY_OPTIONS: ExpiryOption[] = [
	{ key: "1_week", label: "1 Week" },
	{ key: "2_weeks", label: "2 Weeks" },
	{ key: "1_month", label: "1 Month" },
	{ key: "3_months", label: "3 Months" },
]

function getExpiryTimestamp(expiryPeriod: string): number {
	const now = Date.now()
	const oneWeek = 7 * 24 * 60 * 60 * 1000
	const oneMonth = 30 * 24 * 60 * 60 * 1000

	switch (expiryPeriod) {
		case "1_week":
			return Math.floor((now + oneWeek) / 1000)
		case "2_weeks":
			return Math.floor((now + 2 * oneWeek) / 1000)
		case "1_month":
			return Math.floor((now + oneMonth) / 1000)
		case "3_months":
			return Math.floor((now + 3 * oneMonth) / 1000)
		default:
			return Math.floor((now + oneWeek) / 1000)
	}
}
